import { Link, useNavigate } from "react-router-dom";
import Logo from "../../components/Logo";
import OtpInput from "../../components/OtpInput";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useProfessionalAuth } from "../../store/authProfessionalStore";
import { RequiredMark } from "../../components/RequiredMark";

const PENDING_LOGIN_EMAIL_KEY = "agbaza.pro.login.pendingEmail";
const PENDING_RESET_EMAIL_KEY = "agbaza.pro.reset.email";
const PENDING_RESET_OTP_KEY = "agbaza.pro.reset.otp";

type CodePageState = {
  email?: string;
  redirect?: string;
  mode?: "login" | "reset";
};

export default function ProVerificationCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmLoginOtp } = useProfessionalAuth();
  const [code, setCode] = useState("".padEnd(6, ""));
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState<"login" | "reset">("login");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const state = (location.state as CodePageState | null) ?? null;
  const ready = code.replace(/\s/g, "").length === 6;

  useEffect(() => {
    const modeFromState = state?.mode;
    const resolvedMode = modeFromState === "reset" ? "reset" : "login";
    setMode(resolvedMode);

    const storageKey = resolvedMode === "reset" ? PENDING_RESET_EMAIL_KEY : PENDING_LOGIN_EMAIL_KEY;
    const fromState = state?.email ?? "";
    const fromStorage = sessionStorage.getItem(storageKey) ?? "";
    const resolved = fromState || fromStorage;

    if (!resolved) {
      navigate(
        resolvedMode === "reset" ? "/professionnel/mot-de-passe/oubli" : "/professionnel/connexion",
        { replace: true }
      );
      return;
    }
    setEmail(resolved);
    if (!fromStorage && fromState) sessionStorage.setItem(storageKey, fromState);
  }, [navigate, state?.email, state?.mode]);

  const onConfirm = async () => {
    if (!ready || !email) return;
    setError("");
    setSubmitting(true);
    const codeValue = code.replace(/\s/g, "");

    if (mode === "reset") {
      sessionStorage.setItem(PENDING_RESET_OTP_KEY, codeValue);
      setSubmitting(false);
      navigate("/professionnel/mot-de-passe/nouveau", {
        state: { email, otpCode: codeValue },
      });
      return;
    }

    const result = await confirmLoginOtp(email, codeValue);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.message || "Code invalide ou expiré.");
      return;
    }
    sessionStorage.removeItem(PENDING_LOGIN_EMAIL_KEY);
    const redirectTo = state?.redirect;
    const target = redirectTo && redirectTo.startsWith("/pro/") ? redirectTo : "/pro/dashboard/mon-activite";
    navigate(target, { replace: true });
  };

  const onBack = () => {
    if (mode === "reset") {
      sessionStorage.removeItem(PENDING_RESET_EMAIL_KEY);
    } else {
      sessionStorage.removeItem(PENDING_LOGIN_EMAIL_KEY);
    }
  };

  return (
    <div className="relative h-screen overflow-hidden">
      <img
        src={import.meta.env.BASE_URL + 'maps-reset-side.jpg'}
        alt="pro-code-bg"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30" />

      <div className="absolute inset-0 z-10 px-4 md:px-8 py-6 md:py-8 flex">
        <div className="max-w-6xl mx-auto flex flex-col w-full">

          <div className="mt-auto flex items-end justify-between gap-6 pl-2 md:pl-6 pr-4 md:pr-8 pb-4 md:pb-8">
            <div className="w-full max-w-2xl min-h-[520px] bg-white/95 backdrop-blur rounded-[12px] shadow p-8 md:p-10">
              <h1 className="text-2xl font-semibold" style={{ color: '#0B1F16' }}>
                {mode === "reset" ? "Recuperation du mot passe" : "Code de connexion"}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Un code vous a été envoyé sur votre adresse mail <b>{email}</b>.
              </p>
              {error && (
                <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
                  {error}
                </div>
              )}
              <div className="mt-6">
                <div className="text-sm font-semibold mb-2" style={{ color: "#0B1F16" }}>
                  Code de vérification
                  <RequiredMark />
                </div>
                <div className="max-w-sm mx-auto">
                  <OtpInput value={code} onChange={setCode} length={6} fullWidth />
                </div>
                <div className="mt-3 text-sm font-medium" style={{ color: '#00553E' }}>
                  Vous n’avez pas reçu de code ?
                </div>
              </div>
              <div className="mt-6 space-y-3">
                <button
                  className="w-full h-11 rounded-[10px] font-medium cursor-pointer"
                  style={{ backgroundColor: ready ? '#00553E' : '#DCEFE9', color: ready ? '#FFFFFF' : '#7aa79a' }}
                  disabled={!ready || submitting}
                  onClick={onConfirm}
                >
                  {submitting ? "Vérification..." : "Confirmer"}
                </button>
                <Link
                  to={mode === "reset" ? "/professionnel/mot-de-passe/oubli" : "/professionnel/connexion"}
                  className="block"
                  onClick={onBack}
                >
                  <button type="button" className="w-full h-11 rounded-[10px] font-medium border-2 cursor-pointer" style={{ borderColor: '#D1E2DD', color: '#00553E' }}>
                    Retour
                  </button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block shrink-0">
              <img src={import.meta.env.BASE_URL + 'Group 5.png'} alt="agabaza-logo" className="w-auto" style={{ height: 144 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

